import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getProfileAssetUrl, resolveUploadProfileDisplayUrl } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { designationOptions } from '../utils/teacherDesignations';
import { toApiDateOnly, formatDate } from '../utils/appDateTime';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  IndianRupee,
  CreditCard,
  FileText,
  ClipboardList,
  User,
  Camera,
  Edit,
  Save,
  Send,
  Plus,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import MobileInput from './ui/mobile-input';
import DateInput from './ui/date-input';
import { validateMobile } from '../utils/phoneValidation';
import { useAuth } from '../context/AuthContext';
import TeacherPermissionsCard from './TeacherPermissionsCard';
import { openSalarySlipPortal } from '../utils/documentPortalUrl';
import { isNativePlatform } from '../utils/device';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TeacherProfile = ({ editMode = false }) => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState(null);
  const [resendingCreds, setResendingCreds] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [photoCacheKey, setPhotoCacheKey] = useState('');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payForm, setPayForm] = useState({
    month: '',
    year: '',
    basic_salary: '',
    allowances: '0',
    deductions: '0',
    payment_date: '',
    payment_method: 'bank_transfer',
    remarks: '',
  });
  const [payCalcBreakdown, setPayCalcBreakdown] = useState(null);
  const [payCalcLoading, setPayCalcLoading] = useState(false);
  const [attendanceMonth, setAttendanceMonth] = useState(String(new Date().getMonth() + 1));
  const [attendanceYear, setAttendanceYear] = useState(String(new Date().getFullYear()));
  const photoInputRef = React.useRef(null);

  const isAdmin = user?.role_slug === 'school_admin';

  const handleResendCredentials = async () => {
    const uid = profile?.teacher?.user_id;
    if (!uid) return;
    setResendingCreds(true);
    try {
      const res = await api.post('/auth/resend-credentials', { user_id: uid });
      const msg = res?.data?.message || 'New login credentials have been emailed to the user.';
      toast({ title: 'Credentials sent', description: msg });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.message || 'Could not send credentials', variant: 'destructive' });
    } finally {
      setResendingCreds(false);
    }
  };

  useEffect(() => {
    if (id) fetchProfile();
  }, [id, user?.school_id, attendanceMonth, attendanceYear]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      if (id) fetchProfile();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [id, user?.school_id]);

  useEffect(() => {
    const sid = profile?.teacher?.school_id || user?.school_id;
    if (!sid) {
      setSchoolInfo(null);
      return;
    }
    api.get(`schools/${sid}`).then((res) => setSchoolInfo(res.data?.data ?? null)).catch(() => setSchoolInfo(null));
  }, [profile?.teacher?.school_id, user?.school_id]);

  const fetchProfile = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get(`teachers/${id}/profile`, {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
          attendance_month: attendanceMonth,
          attendance_year: attendanceYear,
        },
      });
      const data = response.data.data;
      setProfile(data);
      if (editMode && data?.teacher) {
        const t = data.teacher;
        setFormData({
          first_name: t.first_name || '',
          last_name: t.last_name || '',
          email: t.email || '',
          phone: t.phone || '',
          designation: t.designation || '',
          department: t.department || '',
          qualification: t.qualification || '',
          experience_years: t.experience_years != null ? String(t.experience_years) : '',
          address: t.address || '',
          salary: t.salary != null ? String(t.salary) : '',
          status: t.status || 'active',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load teacher profile',
        variant: 'destructive',
      });
      navigate('/hr/teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData || !id) return;
    const email = formData.email?.trim() || '';
    if (!email) {
      toast({ title: 'Email required', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    const err = validateMobile(formData.phone);
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setSaving(true);
    try {
      await api.put(`teachers/${id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email,
        phone: formData.phone || null,
        designation: formData.designation || null,
        department: formData.department || null,
        qualification: formData.qualification || null,
        experience_years: formData.experience_years ? parseFloat(formData.experience_years) : null,
        address: formData.address || null,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        status: formData.status || null,
      });
      toast({ title: 'Success', description: 'Teacher updated successfully' });
      navigate(`/hr/teachers/${id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update teacher',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const formatMonthYear = (month, year) => {
    if (!month || !year) return '—';
    return `${MONTH_NAMES[Number(month)] || month} ${year}`;
  };

  const handleOpenSalarySlip = async (p) => {
    const slipTab = isNativePlatform() ? null : window.open('', '_blank');
    if (!slipTab && !isNativePlatform()) {
      toast({
        title: 'Could not open slip',
        description: 'Allow pop-ups for this site.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await openSalarySlipPortal(p, api, slipTab);
    } catch (e) {
      toast({
        title: 'Could not open slip',
        description: e?.response?.data?.message || e?.message || 'Try again later',
        variant: 'destructive',
      });
    }
  };

  const openPaySalaryDialog = () => {
    const now = new Date();
    const t = profile?.teacher;
    setPayForm({
      month: String(now.getMonth() + 1),
      year: String(now.getFullYear()),
      basic_salary: t?.salary != null ? String(t.salary) : '',
      allowances: '0',
      deductions: '0',
      payment_date: toApiDateOnly(now),
      payment_method: 'bank_transfer',
      remarks: '',
    });
    setPayCalcBreakdown(null);
    setPayDialogOpen(true);
  };

  const calculateFromAttendance = async () => {
    if (!id) return;
    const month = parseInt(payForm.month, 10);
    const year = parseInt(payForm.year, 10);
    if (month < 1 || month > 12 || year < 2000) {
      toast({ title: 'Invalid period', description: 'Choose a valid month and year.', variant: 'destructive' });
      return;
    }
    setPayCalcLoading(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const res = await api.get(`teachers/${id}/payroll/calculate`, {
        params: {
          month,
          year,
          allowances: parseFloat(payForm.allowances) || 0,
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      const calc = res.data?.data;
      setPayCalcBreakdown(calc);
      if (calc) {
        setPayForm((f) => ({
          ...f,
          basic_salary: String(calc.basic_salary ?? f.basic_salary),
          deductions: String(calc.suggested_deductions ?? f.deductions),
        }));
      }
      toast({ title: 'Calculated', description: 'Amounts updated from attendance records.' });
    } catch (e) {
      toast({ title: 'Calculation failed', description: e.response?.data?.message || 'Could not calculate', variant: 'destructive' });
    } finally {
      setPayCalcLoading(false);
    }
  };

  const payNetPreview = useMemo(() => {
    const basic = parseFloat(payForm.basic_salary) || 0;
    const allowances = parseFloat(payForm.allowances) || 0;
    const deductions = parseFloat(payForm.deductions) || 0;
    return Math.round((basic + allowances - deductions) * 100) / 100;
  }, [payForm.basic_salary, payForm.allowances, payForm.deductions]);

  const handlePaySalarySubmit = async (e) => {
    e.preventDefault();
    if (!id) return;
    const month = parseInt(payForm.month, 10);
    const year = parseInt(payForm.year, 10);
    if (month < 1 || month > 12 || year < 2000) {
      toast({ title: 'Invalid period', description: 'Choose a valid month and year.', variant: 'destructive' });
      return;
    }
    if (payNetPreview < 0) {
      toast({ title: 'Invalid amounts', description: 'Net salary cannot be negative.', variant: 'destructive' });
      return;
    }

    const slipTab = isNativePlatform() ? null : window.open('', '_blank');
    if (!slipTab && !isNativePlatform()) {
      toast({
        title: 'Could not open slip',
        description: 'Allow pop-ups for this site.',
        variant: 'destructive',
      });
      return;
    }
    setPaySubmitting(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const res = await api.post(`teachers/${id}/payroll`, {
        month,
        year,
        basic_salary: parseFloat(payForm.basic_salary) || 0,
        allowances: parseFloat(payForm.allowances) || 0,
        deductions: parseFloat(payForm.deductions) || 0,
        payment_date: payForm.payment_date,
        payment_method: payForm.payment_method,
        remarks: payForm.remarks || null,
        auto_calculate: !!payCalcBreakdown,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      const row = res.data?.data;
      setPayDialogOpen(false);

      if (row?.portal_token || row?.id) {
        try {
          await openSalarySlipPortal(row, api, slipTab);
        } catch (slipErr) {
          toast({
            title: 'Payment saved',
            description: slipErr?.message || 'Open slip from the table when ready.',
          });
        }
      } else {
        try {
          if (slipTab && !slipTab.closed) slipTab.close();
        } catch (_) {
          // ignore
        }
      }

      await fetchProfile();
      toast({ title: 'Salary paid', description: res.data?.message || 'Payment recorded.' });
    } catch (err) {
      if (slipTab && !slipTab.closed) slipTab.close();
      const msg = err.response?.data?.message
        || (err.response?.status === 404 ? 'Pay salary API not found. Deploy latest backend.' : null)
        || (err.response?.status === 403 ? 'Not allowed to record salary.' : null)
        || err.message
        || 'Could not record salary.';
      toast({
        title: 'Payment failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setPaySubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-blue-500" />
      </div>
    );
  }

  if (!profile) return null;

  const { teacher, permissions: userPermissions, salary_payments = [], salary_summary = {}, attendance_summary = {} } = profile;

  const saveExtraPermissions = async (nextExtra) => {
    setPermissionsSaving(true);
    try {
      const res = await api.put(`teachers/${id}/extra-permissions`, { extra_permissions: nextExtra });
      const updated = res.data?.data;
      if (updated) {
        setProfile((prev) => (prev ? { ...prev, permissions: updated } : null));
      }
      toast({ title: 'Saved', description: 'Extra permissions updated' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update permissions',
        variant: 'destructive',
      });
    } finally {
      setPermissionsSaving(false);
    }
  };

  const handleAddPermission = (key) => {
    const current = userPermissions?.extra_permissions || [];
    if (current.includes(key)) return;
    saveExtraPermissions([...current, key]);
  };

  const handleRemovePermission = (key) => {
    const current = userPermissions?.extra_permissions || [];
    saveExtraPermissions(current.filter((p) => p !== key));
  };
  const fullName = [teacher.first_name, teacher.last_name].filter(Boolean).join(' ') || 'Teacher';
  const initials = (teacher.first_name?.[0] || '') + (teacher.last_name?.[0] || '') || '?';
  const avatarUrl = getProfileAssetUrl(teacher.avatar, photoCacheKey);

  const handlePhotoClick = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('photo', prepared);
      const res = await api.post(`teachers/${id}/photo`, formData);
      const uploadData = res.data?.data ?? {};
      const newAvatar = uploadData.avatar;
      if (newAvatar) {
        setPhotoCacheKey(String(Date.now()));
        setProfile((prev) => prev ? { ...prev, teacher: { ...prev.teacher, avatar: newAvatar } } : null);
        if (!resolveUploadProfileDisplayUrl(uploadData) && process.env.NODE_ENV === 'development') {
          console.warn('[TeacherProfile] Photo saved but display URL missing', uploadData);
        }
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="teacher-profile-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-foreground -ml-2"
          onClick={() => navigate(editMode ? `/hr/teachers/${id}` : '/hr/teachers')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {editMode ? 'Cancel (view profile)' : 'Back to Teachers'}
        </Button>
        {!editMode && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-foreground"
                onClick={handleResendCredentials}
                disabled={resendingCreds}
              >
                <Send className="w-4 h-4 mr-2" /> {resendingCreds ? 'Sending...' : 'Resend Credentials'}
              </Button>
            )}
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted hover:text-foreground"
              onClick={() => navigate(`/hr/teachers/${id}/edit`)}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit profile
            </Button>
          </div>
        )}
      </div>

      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-green-800/80 to-card h-24" />
        <CardContent className="relative px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-6 -mt-14">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
            <button
              type="button"
              onClick={handlePhotoClick}
              disabled={uploadingPhoto}
              className="relative h-28 w-28 shrink-0 rounded-xl border-4 border-border shadow-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-background hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Avatar className="h-full w-full rounded-xl">
                <AvatarImage src={avatarUrl} alt={fullName} className="object-cover" />
                <AvatarFallback className="rounded-xl bg-green-600/80 text-xl text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl">
                <Camera className="w-8 h-8 text-foreground" />
              </span>
              {uploadingPhoto && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                  <span className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                </span>
              )}
            </button>
            <div className="flex-1 pt-2">
              {editMode && formData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-muted-foreground">First name</Label>
                      <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Last name</Label>
                      <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-muted text-foreground">
                      {teacher.employee_id} (read-only)
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-muted border-border text-foreground"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <MobileInput value={formData.phone} onChange={(v) => { setFormData({ ...formData, phone: v }); setPhoneError(''); }} error={phoneError} className="bg-muted border-border text-foreground" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-foreground">{fullName}</h1>
                    <Badge variant="secondary" className="bg-muted text-foreground">
                      {teacher.employee_id}
                    </Badge>
                    {teacher.status && (
                      <Badge variant={teacher.status === 'active' ? 'default' : 'outline'}>
                        {teacher.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {teacher.designation || '—'} {teacher.department ? `• ${teacher.department}` : ''}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                    {teacher.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-muted-foreground" /> {teacher.email}
                      </span>
                    )}
                    {teacher.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-muted-foreground" /> {teacher.phone}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {editMode && formData ? (
            <form onSubmit={handleSave} className="space-y-6">
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <User className="w-5 h-5" /> Edit Personal & Professional Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Designation</Label>
                      <Select value={formData.designation} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
                        <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-muted border-border">
                          {designationOptions(formData.designation).map((d) => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Department</Label>
                      <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Qualification</Label>
                      <Input value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Experience (years)</Label>
                      <Input type="number" step="0.5" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Base salary (₹)</Label>
                      <Input type="number" value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-muted border-border">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-muted border-border text-foreground" />
                  </div>
                </CardContent>
              </Card>
              <div className="flex gap-3">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
                <Button type="button" variant="outline" className="border-border text-foreground" onClick={() => navigate(`/hr/teachers/${id}`)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <User className="w-5 h-5" /> Personal & Professional Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Date of birth" value={formatDate(teacher.date_of_birth)} />
              <DetailRow label="Gender" value={teacher.gender || '—'} />
              <DetailRow label="Designation" value={teacher.designation || '—'} />
              <DetailRow label="Department" value={teacher.department || '—'} />
              <DetailRow label="Qualification" value={teacher.qualification || '—'} />
              <DetailRow label="Experience (years)" value={teacher.experience_years ?? '—'} />
              <DetailRow label="Joining date" value={formatDate(teacher.joining_date)} />
              <DetailRow label="Base salary" value={teacher.salary != null ? formatCurrency(teacher.salary) : '—'} />
              {teacher.address && (
                <div className="flex gap-2 pt-1">
                  <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                  <span className="text-foreground">{teacher.address}</span>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          <Card className="border-border">
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Salary Payments
                </CardTitle>
                <p className="text-muted-foreground text-sm mt-1">
                  Total paid: {formatCurrency(salary_summary.total_paid)} ({salary_summary.payment_count || 0} payment(s))
                </p>
              </div>
              {isAdmin && !editMode && (
                <Button type="button" size="sm" onClick={openPaySalaryDialog}>
                  <Plus className="w-4 h-4 mr-1.5" /> Pay salary
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {salary_payments.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">No salary payments recorded yet.</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-foreground text-left">
                        <th className="px-4 py-2 font-medium">Month / Year</th>
                        <th className="px-4 py-2 font-medium">Basic</th>
                        <th className="px-4 py-2 font-medium">Net</th>
                        <th className="px-4 py-2 font-medium">Payment date</th>
                        <th className="px-4 py-2 font-medium">Method</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Slip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salary_payments.map((p) => (
                        <tr key={p.id} className="border-t border-border text-foreground">
                          <td className="px-4 py-2">{formatMonthYear(p.month, p.year)}</td>
                          <td className="px-4 py-2">{formatCurrency(p.basic_salary)}</td>
                          <td className="px-4 py-2 font-medium text-foreground">
                            {formatCurrency(p.net_salary)}
                          </td>
                          <td className="px-4 py-2">{formatDate(p.payment_date)}</td>
                          <td className="px-4 py-2">{p.payment_method || '—'}</td>
                          <td className="px-4 py-2">
                            <Badge variant={p.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                              {p.status || '—'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => handleOpenSalarySlip(p)}
                              className="inline-flex items-center gap-1 text-orange-500 hover:text-orange-400 text-xs"
                            >
                              <FileText className="w-3.5 h-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
            <DialogContent className="border-border bg-card text-foreground max-w-md">
              <DialogHeader>
                <DialogTitle>Pay salary</DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePaySalarySubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Month</Label>
                    <Select
                      value={payForm.month}
                      onValueChange={(v) => setPayForm((f) => ({ ...f, month: v }))}
                    >
                      <SelectTrigger className="bg-muted border-border">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTH_NAMES.slice(1).map((name, i) => (
                          <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Year</Label>
                    <Input
                      type="number"
                      min={2000}
                      max={2100}
                      value={payForm.year}
                      onChange={(e) => setPayForm((f) => ({ ...f, year: e.target.value }))}
                      className="bg-muted border-border"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Basic salary (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={payForm.basic_salary}
                    onChange={(e) => setPayForm((f) => ({ ...f, basic_salary: e.target.value }))}
                    className="bg-muted border-border"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Allowances (₹)</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={payForm.allowances}
                      onChange={(e) => setPayForm((f) => ({ ...f, allowances: e.target.value }))}
                      className="bg-muted border-border"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Deductions (₹)</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={payForm.deductions}
                      onChange={(e) => setPayForm((f) => ({ ...f, deductions: e.target.value }))}
                      className="bg-muted border-border"
                    />
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">Net pay: </span>
                  <span className="font-semibold text-foreground">{formatCurrency(payNetPreview)}</span>
                </div>
                <div className="space-y-1.5">
                  <Label>Payment date</Label>
                  <DateInput
                    value={payForm.payment_date}
                    onChange={(v) => setPayForm((f) => ({ ...f, payment_date: v }))}
                    className="bg-muted border-border"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Payment method</Label>
                  <Select
                    value={payForm.payment_method}
                    onValueChange={(v) => setPayForm((f) => ({ ...f, payment_method: v }))}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" onClick={calculateFromAttendance} disabled={payCalcLoading || paySubmitting}>
                    {payCalcLoading ? 'Calculating…' : 'Calculate from attendance'}
                  </Button>
                </div>
                {payCalcBreakdown ? (
                  <div className="text-xs rounded-md border border-border p-3 space-y-1 text-muted-foreground">
                    <p>Working days: {payCalcBreakdown.working_days} · Present: {payCalcBreakdown.present} · Half-day: {payCalcBreakdown.half_day} · Leave: {payCalcBreakdown.leave} · Absent: {payCalcBreakdown.absent}</p>
                    <p>Suggested deductions: {formatCurrency(payCalcBreakdown.suggested_deductions)} · Net: {formatCurrency(payCalcBreakdown.suggested_net)}</p>
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <Label>Remarks (optional)</Label>
                  <Input
                    value={payForm.remarks}
                    onChange={(e) => setPayForm((f) => ({ ...f, remarks: e.target.value }))}
                    className="bg-muted border-border"
                    placeholder="Optional note"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)} disabled={paySubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={paySubmitting}>
                    {paySubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                      </>
                    ) : (
                      'Record payment'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <ClipboardList className="w-5 h-5" /> Attendance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Month</Label>
                  <Select value={attendanceMonth} onValueChange={setAttendanceMonth}>
                    <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.slice(1).map((name, i) => (
                        <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Year</Label>
                  <Input type="number" value={attendanceYear} onChange={(e) => setAttendanceYear(e.target.value)} className="bg-muted border-border h-9" />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Average attendance</span>
                  <span className="text-foreground font-semibold">
                    {attendance_summary.average_percent ?? 0}%
                  </span>
                </div>
                <Progress value={attendance_summary.average_percent ?? 0} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <StatPill label="Present" value={attendance_summary.present ?? 0} />
                <StatPill label="Absent" value={attendance_summary.absent ?? 0} />
                <StatPill label="Late" value={attendance_summary.late ?? 0} />
                <StatPill label="Half-day" value={attendance_summary.half_day ?? 0} />
                <StatPill label="Leave" value={attendance_summary.leave ?? 0} />
                <StatPill label="Total days" value={attendance_summary.total_days ?? 0} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <IndianRupee className="w-5 h-5" /> Salary Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(salary_summary.total_paid)}
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                {salary_summary.payment_count ?? 0} payment(s) recorded
              </p>
            </CardContent>
          </Card>

          {isAdmin && userPermissions && !editMode && (
            <TeacherPermissionsCard
              permissions={userPermissions}
              canEdit={userPermissions.can_have_extra}
              saving={permissionsSaving}
              onAdd={handleAddPermission}
              onRemove={handleRemovePermission}
            />
          )}
        </div>
      </div>
    </div>
  );
};

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-muted/50 rounded-lg px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground font-semibold">{value}</p>
    </div>
  );
}

export default TeacherProfile;
